import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { usePath, navigate, parseRoute } from '../../lib/router';
import Modal from '../shared/Modal';
import { Plus, X, Edit3, RefreshCw, Shield, Search, Mail, CheckCircle, Building, User, AlertTriangle, Link as LinkIcon, Ban, LogOut, Eye, Users, Briefcase, FileText, MapPin, ArrowUp, ArrowDown, Trash2, Send, Archive, ArchiveRestore, Crown, UserCheck } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import { getActiveWorkspaceId } from '../../lib/workspaces';
import { useWorkspaces } from '../shared/WorkspaceProvider';
import { US_STATES } from '../../lib/tippValidation';
import { AdminOrgInviteModal } from './AdminOrgInviteModal';
import OrgDocumentsSection from './OrgDocumentsSection';
import OrgSpecialRequestsSection from './OrgSpecialRequestsSection';
import { ListPagination, useListPagination } from './ListPagination';

interface SponsorRecord {
  sponsorId: string;
  name: string;
  tier: 'rti' | 'batch';
  active: boolean;
  contactEmail?: string;
  sevisProgramNumber?: string;
  sevisUserId?: string;
  supportedCategories: string[];
  defaultExportFormat?: 'packet' | 'batch';
  assignedUserIds?: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
  archived?: boolean;
  archivedAt?: string | null;
}

const SUPPORTED_CATEGORIES = ['Intern', 'Trainee', 'Student Intern', 'Teacher', 'Camp Counselor', 'Summer Work Travel', 'Au Pair', 'Professor', 'Research Scholar', 'Short-Term Scholar', 'Government Visitor', 'Secondary School Student'];

interface SponsorsManagerProps {
  canEdit?: boolean;
}

export default function SponsorsManager({ canEdit = true }: SponsorsManagerProps = {}) {
  const [sponsors, setSponsors] = useState<SponsorRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  useBodyScrollLock(showModal || showInviteModal);
  const [editing, setEditing] = useState<SponsorRecord | null>(null);
  const [linkModal, setLinkModal] = useState<{ sponsorId: string; name: string; email: string } | null>(null);
  const [linkEmail, setLinkEmail] = useState('');
  const [linking, setLinking] = useState(false);
  const [detailSponsor, setDetailSponsor] = useState<{ sponsor: any; linkedUsers: any[]; applications: any[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  // Sync detailSponsor with URL path.
  //
  // #1125 Item 5 (sibling of the EmployersManager fix): the detail state
  // must not be a dependency of this route-sync effect — opening the
  // detail from the list set the state while the path was still
  // '/sponsors', the effect re-ran on that commit and cleared it, and
  // the navigate effect bounced back, so the detail could never
  // stabilise. Compare ids through a ref; the effect is route-driven.
  const detailSponsorIdRef = useRef<string | null>(null);
  useEffect(() => {
    detailSponsorIdRef.current = detailSponsor?.sponsor?.sponsorId ?? null;
  }, [detailSponsor]);

  useEffect(() => {
    if (currentRoute.type === 'sponsors_detail') {
      const targetId = currentRoute.id;
      if (detailSponsorIdRef.current !== targetId) {
        setDetailLoading(true);
        safeFetch(`/api/admin/sponsors/${targetId}/detail`)
          .then((data) => {
            setDetailSponsor(data);
          })
          .catch((err) => {
            console.error('Failed to fetch sponsor details:', err);
          })
          .finally(() => {
            setDetailLoading(false);
          });
      }
    } else if (currentPath === '/sponsors') {
      if (detailSponsorIdRef.current) {
        setDetailSponsor(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const prevDetailSponsorRef = useRef(detailSponsor);

  // Sync URL path when detailSponsor changes
  useEffect(() => {
    if (detailSponsor?.sponsor?.sponsorId) {
      navigate(`/sponsors/adminview/${detailSponsor.sponsor.sponsorId}`);
    } else if (prevDetailSponsorRef.current && !detailSponsor && currentRoute.type === 'sponsors_detail') {
      navigate('/sponsors');
    }
    prevDetailSponsorRef.current = detailSponsor;
  }, [detailSponsor, currentRoute.type]);

  const [form, setForm] = useState({
    name: '', tier: 'rti' as 'rti' | 'batch', contactEmail: '',
    sevisProgramNumber: '', sevisUserId: '',
    supportedCategories: [] as string[],
    defaultExportFormat: 'packet' as 'packet' | 'batch',
    notes: '', active: true,
  });

  const showToast = (message: string, type: 'success' | 'error') => {
    notifyToast(type, message);
  };

  // Follow-up Item 12: gate the initial fetch on workspace resolution.
  // workspaceHeader() reads localStorage at call time; fetching before
  // WorkspaceProvider validated the stored id reads the WRONG workspace
  // context and the list sticks at stale zeros until a manual Refresh.
  const { loading: workspaceLoading, activeWorkspaceId } = useWorkspaces();
  const fetchSponsors = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safeFetch('/api/admin/sponsors');
      setSponsors(data.sponsors || []);
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);

  useEffect(() => { if (!workspaceLoading) fetchSponsors(); }, [fetchSponsors, workspaceLoading]);

  const resetForm = () => {
    setForm({
      name: '', tier: 'rti', contactEmail: '',
      sevisProgramNumber: '', sevisUserId: '',
      supportedCategories: [],
      defaultExportFormat: 'packet',
      notes: '', active: true,
    });
    setEditing(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editing) {
        await safeFetch(`/api/admin/sponsors/${editing.sponsorId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        showToast('Sponsor updated', 'success');
      } else {
        await safeFetch('/api/admin/sponsors', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        showToast('Sponsor created', 'success');
      }
      setShowModal(false);
      resetForm();
      fetchSponsors();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleDelete = async (sponsorId: string) => {
    if (!(await confirmDialog('Delete this sponsor? This cannot be undone.'))) return;
    try {
      await safeFetch(`/api/admin/sponsors/${sponsorId}`, { method: 'DELETE' });
      showToast('Sponsor deleted', 'success');
      fetchSponsors();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleToggleActive = async (s: SponsorRecord) => {
    if (!(await confirmDialog(s.active
      ? `Deactivate the sponsor ${s.name}? The sponsor will be marked inactive. You can reactivate it at any time.`
      : `Reactivate the sponsor ${s.name}? The sponsor will be marked active again.`))) return;
    try {
      await safeFetch(`/api/admin/sponsors/${s.sponsorId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...s, active: !s.active }),
      });
      showToast(`Sponsor ${s.active ? 'deactivated' : 'activated'}`, 'success');
      fetchSponsors();
    } catch (err: any) { showToast(err.message, 'error'); }
  };

  const handleLinkUser = async () => {
    if (!linkModal || !linkEmail.trim()) return;
    setLinking(true);
    try {
      await safeFetch(`/api/admin/sponsors/${linkModal.sponsorId}/link-user`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: linkEmail.trim().toLowerCase() }),
      });
      showToast(`User linked to ${linkModal.name}`, 'success');
      setLinkModal(null); setLinkEmail('');
      fetchSponsors();
    } catch (err: any) { showToast(err.message, 'error'); }
    finally { setLinking(false); }
  };

  const filtered = sponsors.filter(s => {
    // Archived orgs are hidden from the main list; the Archived view shows only them.
    // Approved AND rejected sponsors remain visible until explicitly archived.
    if (!!s.archived !== showArchived) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const idStr = (s.sponsorId || ('SPO-' + (s.numericId || ''))).toLowerCase();
    return idStr.includes(q) || (s.name || '').toLowerCase().includes(q) || (s.contactEmail || '').toLowerCase().includes(q);
  });

  // Client-side paging over the full filtered set (the endpoint returns
  // every sponsor; see ListPagination for the shared pattern). Resets to
  // page 1 when the search text or Active/Archived view changes.
  const pager = useListPagination(filtered, `${searchQuery}|${showArchived}`);

  // Archive / unarchive: archived sponsors are hidden from the main list but stay
  // fully accessible via the Archived view.
  const handleArchiveToggle = async (sponsorId: string, archive: boolean, name?: string) => {
    const label = name || 'this sponsor';
    if (!(await confirmDialog(archive
      ? `Archive ${label}? It will be hidden from the active sponsor list. You can restore it from the archive at any time.`
      : `Restore ${label} from the archive? It will return to the active sponsor list.`))) return;
    try {
      await safeFetch(`/api/admin/sponsors/${sponsorId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          archived: archive,
          archivedAt: archive ? new Date().toISOString() : null,
        }),
      });
      setSponsors(prev => prev.map(s => (s.sponsorId === sponsorId ? { ...s, archived: archive, archivedAt: archive ? new Date().toISOString() : null } : s)));
      showToast(archive ? 'Sponsor archived' : 'Sponsor restored from archive', 'success');
    } catch (err: any) { showToast(err.message || 'Failed to update archive status', 'error'); }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toasts render via the global root-level notification layer. */}

      {/* Header */}
      <div className="p-6 pb-0 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-brand" />
          <h1 className="text-lg font-black text-slate-900">Sponsors</h1>
          <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{sponsors.length}</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search sponsors..." className="pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm w-56 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
          </div>
          <div className="flex items-center rounded-xl border border-slate-200 overflow-hidden text-xs font-bold">
            <button onClick={() => setShowArchived(false)} className={`px-3 py-2 transition cursor-pointer ${!showArchived ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>Active</button>
            <button onClick={() => setShowArchived(true)} className={`px-3 py-2 transition cursor-pointer flex items-center gap-1 ${showArchived ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}><Archive className="h-3.5 w-3.5" />Archived ({sponsors.filter(s => s.archived).length})</button>
          </div>
          {canEdit && (
            <>
              <button onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-1.5 px-4 py-2 btn-primary text-white rounded-xl text-xs font-bold  cursor-pointer">
                <Plus className="h-3.5 w-3.5" /> Add Sponsor
              </button>
              <button onClick={() => setShowInviteModal(true)} className="flex items-center gap-1.5 px-4 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-xl text-xs font-bold cursor-pointer">
                <Send className="h-3.5 w-3.5" /> Invite Sponsor
              </button>
            </>
          )}
          <button aria-label="Refresh sponsors" title="Refresh sponsors" onClick={fetchSponsors} className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 cursor-pointer"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Sponsor</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Tier</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Categories</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">SEVIS ID</th>
                <th className="text-left px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">No sponsors found.</td></tr>
              ) : pager.pageItems.map(s => (
                <tr key={s.sponsorId} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center text-brand font-black text-xs">{s.name.charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="font-bold text-slate-900">{s.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{s.sponsorId}</div>
                        {s.contactEmail && <div className="text-[11px] text-slate-500">{s.contactEmail}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${s.tier === 'batch' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-brand'}`}>
                      {s.tier === 'batch' ? 'Batch' : 'RTI'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(s.supportedCategories || []).slice(0, 3).map(c => (
                        <span key={c} className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded font-medium text-slate-600">{c}</span>
                      ))}
                      {(s.supportedCategories || []).length > 3 && (
                        <span className="text-[11px] text-slate-500">+{s.supportedCategories.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 font-mono">{s.sevisUserId || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${s.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {s.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={async () => {
                        setDetailLoading(true);
                        try {
                          const data = await safeFetch(`/api/admin/sponsors/${s.sponsorId}/detail`);
                          setDetailSponsor(data);
                        } catch (err: any) { showToast(err.message, 'error'); }
                        finally { setDetailLoading(false); }
                      }} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 cursor-pointer" title="View Details"><Eye className="h-3.5 w-3.5" /></button>
                      <button onClick={() => { setLinkModal({ sponsorId: s.sponsorId, name: s.name, email: s.contactEmail || '' }); setLinkEmail(s.contactEmail || ''); }} className="p-1.5 rounded-lg hover:bg-violet-50 text-violet-600 cursor-pointer" title="Link User"><LinkIcon className="h-3.5 w-3.5" /></button>
                      <button onClick={() => { setEditing(s); setForm({ name: s.name, tier: s.tier, contactEmail: s.contactEmail || '', sevisProgramNumber: s.sevisProgramNumber || '', sevisUserId: s.sevisUserId || '', supportedCategories: s.supportedCategories || [], defaultExportFormat: s.defaultExportFormat || 'packet', notes: s.notes || '', active: s.active }); setShowModal(true); }} className="p-1.5 rounded-lg bg-brand-active text-brand cursor-pointer" title="Edit"><Edit3 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleToggleActive(s)} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 cursor-pointer" title={s.active ? 'Deactivate' : 'Activate'}>{s.active ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle className="h-3.5 w-3.5" />}</button>
                      {s.archived ? (
                        <button onClick={() => handleArchiveToggle(s.sponsorId, false, s.name)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 cursor-pointer" title="Restore from archive"><ArchiveRestore className="h-3.5 w-3.5" /></button>
                      ) : (
                        <button onClick={() => handleArchiveToggle(s.sponsorId, true, s.name)} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 cursor-pointer" title="Archive"><Archive className="h-3.5 w-3.5" /></button>
                      )}
                      <button onClick={() => handleDelete(s.sponsorId)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 cursor-pointer" title="Delete"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <ListPagination
            page={pager.page}
            pageCount={pager.pageCount}
            total={pager.total}
            start={pager.start}
            end={pager.end}
            pageSize={pager.pageSize}
            onPageChange={pager.setPage}
            onPageSizeChange={pager.setPageSize}
            itemLabel="sponsors"
          />
        )}
      </div>

      {/* Link User Modal */}
      {linkModal && (
        <Modal onClose={() => { setLinkModal(null); }} ariaLabel="Link User to Sponsor">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-black text-slate-900">Link User to Sponsor</h2>
              <button aria-label="Close" title="Close" onClick={() => setLinkModal(null)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer"><X className="h-5 w-5 text-slate-500" /></button>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              Link a user account to <strong>{linkModal.name}</strong>. The user will log in with this email and access
              the sponsor dashboard. If the user does not exist, an account will be created for them.
            </div>
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1.5">User Email</label>
              <input type="email" required value={linkEmail} onChange={e => setLinkEmail(e.target.value)} placeholder="user@example.com" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setLinkModal(null)} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 cursor-pointer">Cancel</button>
              <button type="button" onClick={handleLinkUser} disabled={linking || !linkEmail.trim()} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white btn-primary  disabled:opacity-50 cursor-pointer">
                {linking ? 'Linking...' : 'Link User'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal onClose={() => { if (!editing) { setShowModal(false); resetForm(); } }} ariaLabel={editing ? 'Edit Sponsor' : 'Add Sponsor'}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-900">{editing ? 'Edit Sponsor' : 'Add Sponsor'}</h3>
              <button aria-label="Close" title="Close" onClick={() => { setShowModal(false); resetForm(); }} className="p-1 rounded hover:bg-slate-100 text-slate-500 cursor-pointer"><X className="h-5 w-5" /></button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Sponsor Name *</label>
                  <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 " />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Tier *</label>
                  <select value={form.tier} onChange={e => setForm(f => ({ ...f, tier: e.target.value as any }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="rti">RTI (Individual Packet)</option>
                    <option value="batch">Batch (SEVIS XML)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Default Export Format</label>
                  <select value={form.defaultExportFormat} onChange={e => setForm(f => ({ ...f, defaultExportFormat: e.target.value as any }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option value="packet">Packet (JSON)</option>
                    <option value="batch">Batch (XML/ZIP)</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Contact Email</label>
                  <input type="email" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">SEVIS Program Number</label>
                  <input value={form.sevisProgramNumber} onChange={e => setForm(f => ({ ...f, sevisProgramNumber: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" placeholder="P-1-XXXXX" />
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">SEVIS User ID</label>
                  <input value={form.sevisUserId} onChange={e => setForm(f => ({ ...f, sevisUserId: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Supported Categories</label>
                  <div className="flex flex-wrap gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                    {SUPPORTED_CATEGORIES.map(cat => (
                      <label key={cat} className="flex items-center gap-1.5 text-xs font-medium cursor-pointer">
                        <input type="checkbox" checked={form.supportedCategories.includes(cat)} onChange={e => setForm(f => ({ ...f, supportedCategories: e.target.checked ? [...f.supportedCategories, cat] : f.supportedCategories.filter(c => c !== cat) }))} className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary" />
                        {cat}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Notes</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="sponsor-active" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary" />
                  <label htmlFor="sponsor-active" className="text-xs font-medium">Active</label>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 cursor-pointer">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white btn-primary  cursor-pointer">{editing ? 'Update' : 'Create'} Sponsor</button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Sponsor Detail Modal */}
      {detailSponsor && (
        <Modal onClose={() => setDetailSponsor(null)} ariaLabel="Sponsor Details">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-base font-black text-slate-900">{detailSponsor.sponsor?.name || 'Sponsor'}</h2>
                <p className="text-xs text-slate-500">Sponsor details &amp; linked records</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setDetailSponsor(null)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-all cursor-pointer">
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Sponsor Info */}
              <div className="grid grid-cols-3 gap-4 bg-slate-50 rounded-xl p-4">
                <div><label className="text-[10px] uppercase font-bold text-slate-400 block">Tier</label><p className="text-xs font-medium text-slate-700 capitalize">{detailSponsor.sponsor?.tier || 'rti'}</p></div>
                <div><label className="text-[10px] uppercase font-bold text-slate-400 block">Email</label><p className="text-xs text-slate-600">{detailSponsor.sponsor?.contactEmail || '—'}</p></div>
                <div><label className="text-[10px] uppercase font-bold text-slate-400 block">Status</label>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${detailSponsor.sponsor?.status === 'active' || detailSponsor.sponsor?.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {detailSponsor.sponsor?.status === 'active' || detailSponsor.sponsor?.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="col-span-3"><label className="text-[10px] uppercase font-bold text-slate-400 block">SEVIS Program #</label><p className="text-xs font-mono text-slate-600">{detailSponsor.sponsor?.sevisProgramNumber || '—'}</p></div>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase font-bold text-slate-400 block">Categories</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(detailSponsor.sponsor?.supportedCategories || []).map((c: string) => (
                      <span key={c} className="px-2 py-0.5 bg-brand-active rounded text-[10px] font-semibold">{c}</span>
                    ))}
                    {(!detailSponsor.sponsor?.supportedCategories || detailSponsor.sponsor.supportedCategories.length === 0) && (
                      <span className="text-xs text-slate-500">None</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Organization documents (Slice 1) */}
              <OrgDocumentsSection orgType="sponsor" orgId={detailSponsor.sponsor.sponsorId} canEdit={canEdit} />

              {/* Organization special requests (Slice 2) */}
              <OrgSpecialRequestsSection orgType="sponsor" orgId={detailSponsor.sponsor.sponsorId} canEdit={canEdit} />

              {/* Linked Users */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-indigo-600" />
                    Linked Users ({(detailSponsor.linkedUsers || []).length})
                  </h3>
                  <button
                    onClick={() => { setLinkModal({ sponsorId: detailSponsor.sponsor.sponsorId, name: detailSponsor.sponsor.name, email: detailSponsor.sponsor.contactEmail || '' }); setLinkEmail(''); }}
                    className="px-2.5 py-1 text-[10px] font-bold text-indigo-600 hover:bg-indigo-50 rounded-lg transition cursor-pointer flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add User
                  </button>
                </div>
                {(!detailSponsor.linkedUsers || detailSponsor.linkedUsers.length === 0) ? (
                  <p className="text-xs text-slate-500 text-center py-3">No users linked to this sponsor</p>
                ) : (
                  <div className="space-y-2">
                    {detailSponsor.linkedUsers.filter((u: any) => u.status !== 'removed').map((u: any) => (
                      <div key={u.uid} className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5 border border-slate-200">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                            <User className="h-3.5 w-3.5 text-indigo-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-800 truncate">{u.displayName || u.email}</p>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500">
                              <span className={`px-1.5 py-0.5 rounded font-medium ${
                                u.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                u.status === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'
                              }`}>{u.status}</span>
                              <span className={`px-1.5 py-0.5 rounded font-medium ${u.agentRole === 'super' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                {u.agentRole === 'super' ? 'Super Agent' : 'Regular Agent'}
                              </span>
                              {(detailSponsor.sponsor as any)?.userId && u.uid === (detailSponsor.sponsor as any).userId && (
                                <span className="px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700" title="Designated Super Agent — protected from removal/demotion; use Make Super Agent on another member to change">Owner</span>
                              )}
                              {u.invitedAt && <span>Since {new Date(u.invitedAt).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {u.status === 'active' && u.agentRole === 'regular' && (
                            <button onClick={async () => {
                              try {
                                await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/user-action`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ uid: u.uid, action: 'promote-agent' }),
                                });
                                showToast('Promoted to Super Agent', 'success');
                                const data = await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/detail`);
                                setDetailSponsor(data);
                              } catch (err: any) { showToast(err.message, 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition cursor-pointer" title="Promote to Super Agent">
                              <ArrowUp className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.agentRole === 'super' && u.uid !== (detailSponsor.sponsor as any)?.userId && detailSponsor.linkedUsers.filter((lu: any) => lu.agentRole === 'super' && lu.status !== 'removed').length > 1 && (
                            <button onClick={async () => {
                              try {
                                await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/user-action`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ uid: u.uid, action: 'demote-agent' }),
                                });
                                showToast('Demoted to Regular Agent', 'success');
                                const data = await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/detail`);
                                setDetailSponsor(data);
                              } catch (err: any) { showToast(err.message, 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition cursor-pointer" title="Demote to Regular Agent">
                              <ArrowDown className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.uid !== (detailSponsor.sponsor as any)?.userId && (
                            <button onClick={async () => {
                              if (!(await confirmDialog(`Make ${u.email} the Super Agent for this sponsor?\n\nThe current Super Agent becomes a regular agent and loses Super Agent protection (they can then be removed, demoted, or deactivated normally). ${u.email} gains that protection. This is the only way the Super Agent designation changes.`))) return;
                              try {
                                await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/change-super`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ uid: u.uid }),
                                });
                                showToast('Super Agent changed', 'success');
                                const data = await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/detail`);
                                setDetailSponsor(data);
                              } catch (err: any) { showToast(err.message, 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-600 transition cursor-pointer" title="Make Super Agent (formal change)">
                              <Crown className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'active' && u.uid !== (detailSponsor.sponsor as any)?.userId && (
                            <button onClick={async () => {
                              try {
                                await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/user-action`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ uid: u.uid, action: 'block' }),
                                });
                                showToast('User deactivated', 'success');
                                const data = await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/detail`);
                                setDetailSponsor(data);
                              } catch (err: any) { showToast(err.message, 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition cursor-pointer" title="Deactivate (admin) — the org super cannot reactivate over this">
                              <Ban className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.status === 'blocked' && (
                            <button onClick={async () => {
                              try {
                                await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/user-action`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ uid: u.uid, action: 'unblock' }),
                                });
                                showToast('User reactivated', 'success');
                                const data = await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/detail`);
                                setDetailSponsor(data);
                              } catch (err: any) { showToast(err.message, 'error'); }
                            }} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600 transition cursor-pointer" title="Reactivate">
                              <UserCheck className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {u.uid !== (detailSponsor.sponsor as any)?.userId && (
                          <button onClick={async () => {
                            if (!(await confirmDialog(`Remove ${u.email} from this sponsor?`))) return;
                            try {
                              await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/user-action`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ uid: u.uid, action: 'remove' }),
                              });
                              showToast('User removed from sponsor', 'success');
                              const data = await safeFetch(`/api/admin/sponsors/${detailSponsor.sponsor.sponsorId}/detail`);
                              setDetailSponsor(data);
                            } catch (err: any) { showToast(err.message, 'error'); }
                          }} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition cursor-pointer" title="Remove from sponsor">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Applications */}
              <div>
                <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-indigo-600" />
                  Applications ({(detailSponsor.applications || []).length})
                </h3>
                {detailLoading ? (
                  <div className="py-10 text-center text-xs text-slate-500">Loading...</div>
                ) : !detailSponsor.applications || detailSponsor.applications.length === 0 ? (
                  <div className="py-10 text-center">
                    <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No applications assigned to this sponsor</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {detailSponsor.applications.slice(0, 20).map((app: any) => (
                      <div key={app.applicationId} className="border border-slate-200 rounded-xl p-3 hover:shadow-sm transition-all">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="text-xs font-bold text-slate-800">{app.applicantName}</h4>
                              <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-600">{app.programType || '—'}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                              <span className="flex items-center gap-1">{app.sponsorshipStatus || 'unassigned'}</span>
                              {app.updatedAt && <span>Updated {new Date(app.updatedAt).toLocaleDateString()}</span>}
                            </div>
                          </div>
                          <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-slate-100 text-slate-600">{app.status || ''}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Invite Sponsor Modal */}
      <AdminOrgInviteModal
        isOpen={showInviteModal}
        role="sponsor"
        onClose={() => setShowInviteModal(false)}
        onSuccess={() => {
          setShowInviteModal(false);
          notifyToast('success', 'Invitation sent successfully!');
          fetchSponsors();
        }}
        workspaceId={getActiveWorkspaceId() || ''}
        canEdit={canEdit}
      />
    </div>
  );
}
